<script setup lang="ts">
// The five pane states, written once (contract 22 §10, master-plan §7). Unlike the other two kit
// members this has NO legacy ancestor: today no pane in 22 implements loading, empty, error or
// forbidden at all — `Notifications.vue:76` and `UsersPermissions.vue:367` call and hope. So the
// shell is net-new, and 30's and 23's registered panes reuse it verbatim rather than each inventing
// a fifth variant of "something went wrong".
//
// Three rules the states encode, all from §14/§12:
//   * empty is a DOMAIN empty state, never a toast — hence `emptyKey`, so "no channels configured"
//     and "no members yet" do not collapse into one generic sentence;
//   * error is an inline Alert with Retry, never a toast — `retry` is the pane's reload;
//   * forbidden renders instead of redirecting, and offers NO retry: retrying a permission you do
//     not have just fails again.
import type { SettingsPaneState } from '../../models/settingsPane.types'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Skeleton } from '@/shared/ui'

withDefaults(
  defineProps<{
    state: SettingsPaneState
    /** i18n key for the load failure. Panes with a specific diagnosis pass their own. */
    errorKey?: string
    /** i18n key for the empty state — what is missing, in the PANE's words. */
    emptyKey?: string
  }>(),
  { errorKey: 'workspaceSettings.pane.loadFailed', emptyKey: 'workspaceSettings.pane.empty' },
)

const emit = defineEmits<{ retry: [] }>()
const { t } = useI18n()

/** Settings rows are uniform, so one placeholder shape serves every pane (§10). */
const PLACEHOLDER_ROWS = 4
</script>

<template>
  <div
    v-if="state === 'loading'"
    class="pane-shell__loading flex flex-col border border-border rounded-lg bg-surface overflow-hidden"
    aria-busy="true"
    data-testid="settings-pane-loading"
  >
    <div
      v-for="row in PLACEHOLDER_ROWS"
      :key="row"
      class="pane-shell__loading-row grid grid-cols-[minmax(0,_3fr)_minmax(0,_7fr)_auto] items-center gap-md py-md px-lg border-t border-t-border [&:first-child]:border-t-0"
    >
      <Skeleton width="9rem" height="1rem" />
      <Skeleton width="15rem" height="1rem" />
      <Skeleton width="4.5rem" height="2rem" radius="var(--ui-radius-md)" />
    </div>
  </div>

  <Alert v-else-if="state === 'forbidden'" tone="warning" data-testid="settings-pane-forbidden">
    {{ t('workspaceSettings.error.forbidden') }}
  </Alert>

  <Alert v-else-if="state === 'error'" tone="danger" data-testid="settings-pane-error">
    <div class="pane-shell__error flex flex-wrap items-center justify-between gap-md">
      <span>{{ t(errorKey) }}</span>
      <Button variant="outline" size="sm" data-testid="settings-pane-retry" @click="emit('retry')">
        {{ t('workspaceSettings.error.retry') }}
      </Button>
    </div>
  </Alert>

  <section
    v-else-if="state === 'empty'"
    class="pane-shell__empty p-xl border border-border rounded-lg bg-surface text-center"
    role="status"
    data-testid="settings-pane-empty"
  >
    <p class="pane-shell__empty-text m-0 text-muted font-sans text-[0.875rem]">
      {{ t(emptyKey) }}
    </p>
  </section>

  <div
    v-else
    class="pane-shell__ready flex flex-col border border-border rounded-lg bg-surface overflow-hidden"
    data-testid="settings-pane-ready"
  >
    <slot />
  </div>
</template>

<style scoped>
.pane-shell__loading .pane-shell__ready > :first-child {
  border-top-style: solid;
  border-top-width: 0px;
}

.pane-shell__ready > :first-child {
  border-top-style: solid;
  border-top-width: 0px;
}

.pane-shell__empty .pane-shell__ready > :first-child {
  border-top-style: solid;
  border-top-width: 0px;
}

.pane-shell__ready > :first-child {
  border-top-style: solid;
  border-top-width: 0px;
}
</style>
